import React from "react";
import Dialog from "@material-ui/core/Dialog";
import styled from "styled-components";
import { tokens } from "../../theme/tokens";

// Modal — branded wrapper over MUI v4 Dialog (keeps focus-trap / esc / backdrop
// a11y behaviour, restyles the surface to the design system).
//
//   <Modal open={open} onClose={close} title="Confirm" subtitle="This can't be undone"
//          actions={<><Button variant="ghost" onClick={close}>Cancel</Button>
//                     <Button variant="danger" onClick={go}>Delete</Button></>}>
//      Are you sure?
//   </Modal>

const { colors, radii, shadows, spacing, typography } = tokens;

const dialogPaperProps = {
    style: {
        borderRadius: radii.lg,
        boxShadow: shadows.xl,
        overflow: "hidden",
        background: colors.surface,
        // widen MUI's default cramped paper a little
        margin: 16,
        width: "100%",
    },
};

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${spacing[4]};
    padding: ${spacing[5]} ${spacing[5]} ${spacing[4]};
    border-bottom: 1px solid ${colors.divider};
`;

const Titles = styled.div`
    h3 {
        margin: 0;
        font-family: ${typography.fonts.body};
        font-size: ${typography.size.xl};
        font-weight: ${typography.weight.bold};
        letter-spacing: ${typography.tracking.tight};
        color: ${colors.heading};
    }
    p {
        margin: 6px 0 0;
        font-size: ${typography.size.base};
        color: ${colors.muted};
        line-height: ${typography.leading.snug};
    }
`;

const CloseBtn = styled.button`
    appearance: none;
    border: 0;
    background: transparent;
    color: ${colors.muted};
    width: 34px;
    height: 34px;
    border-radius: ${radii.sm};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
    &:hover { background: ${colors.surfaceSunken}; color: ${colors.heading}; }
`;

const Body = styled.div`
    padding: ${spacing[5]};
    font-size: ${typography.size.base};
    color: ${colors.body};
    line-height: ${typography.leading.normal};
    max-height: 70vh;
    overflow-y: auto;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${spacing[2]};
    padding: ${spacing[4]} ${spacing[5]};
    border-top: 1px solid ${colors.divider};
    background: ${colors.cream};
`;

export default function Modal({
    open,
    onClose,
    title,
    subtitle,
    actions,
    children,
    maxWidth = "sm",
    showClose = true,
    ...rest
}) {
    return (
        <Dialog
            open={!!open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth
            PaperProps={dialogPaperProps}
            {...rest}
        >
            {(title || showClose) && (
                <Header>
                    <Titles>
                        {title && <h3>{title}</h3>}
                        {subtitle && <p>{subtitle}</p>}
                    </Titles>
                    {showClose && onClose && (
                        <CloseBtn type="button" aria-label="Close" onClick={onClose}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path
                                    d="M4 4l10 10M14 4L4 14"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </CloseBtn>
                    )}
                </Header>
            )}
            <Body>{children}</Body>
            {actions && <Footer>{actions}</Footer>}
        </Dialog>
    );
}
